import { CubePresenter, FlipIntent, Logger, NOOP_VOID, ScopeSlot } from 'src/3rds/cube'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils'
import { ServiceError } from 'src/utils/ServiceError'
import { TemplatesKeys } from './Templates.keys'
import { editorDoTemplate, editorNovo, podeExcluir, problemasDoEditor } from './Templates.regras'
import { Editor, TemplatesScope } from './Templates.scopes'
import { TemplatesService } from './Templates.service'

const LOG = Logger.get('TemplatesPresenter')
const service = TemplatesService.singleton()
const appStorage = AppStorage.singleton()

// Um template muda o que a LIA manda a todos os parceiros e pesa na reputação do número: só admin, como no servidor.
export const ROLES_AUTORIZADOS = new Set(['admin'])

export function podeGerirTemplates() {
    return ROLES_AUTORIZADOS.has(`${appStorage.userRole ?? ''}`)
}

function mensagemDeNegocio(error: unknown): string | undefined {
    if (error instanceof ServiceError && [400, 404].includes(error.statusCode)) {
        return error.message
    }
    return undefined
}

/** Templates do WhatsApp (E24): os da conta na Meta e qual atende cada uso da LIA. */
export class TemplatesPresenter extends CubePresenter<MainPresenter, TemplatesScope> {
    private parentSlot: ScopeSlot = NOOP_VOID

    constructor(app: MainPresenter) {
        super(app, new TemplatesScope())
    }

    public override release() {
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        const keys = new TemplatesKeys(this.app, intent)
        if (initialization) {
            if (!podeGerirTemplates()) {
                this.app.alert('warning', 'Acesso restrito', 'Os templates são restritos a administradores.')
                keys.allow = false
                return keys.allow
            }
            this.parentSlot = keys.parentSlot
            this.#initialize()
            await this.#carregar(false)
        }
        this.parentSlot(this.scope)
        return keys.allow
    }

    #initialize() {
        const s = this.scope
        s.onAtualizar = this.action(() => this.#carregar(true), this)
        s.onBuscaChange = this.action((busca: string) => (s.busca = busca), this)
        s.onMostrarExemplosChange = this.action((mostrar: boolean) => (s.mostrarExemplosMeta = mostrar), this)

        s.onAbrirDetalhe = this.action((id: string) => (s.detalhe = s.templates.find((t) => t.id === id)), this)
        s.onFecharDetalhe = this.action(() => (s.detalhe = undefined), this)

        s.onAbrirCriar = this.action(this.#abrirCriar, this)
        s.onAbrirEditar = this.action(this.#abrirEditar, this)
        s.onEditorChange = this.action((mudanca: Partial<Editor>) => {
            if (s.editor) s.editor = { ...s.editor, ...mudanca }
        }, this)
        s.onFecharEditor = this.action(() => {
            s.editor = undefined
            s.erroEditor = undefined
        }, this)
        s.onSalvarEditor = this.action(this.#salvarEditor, this)

        s.onAbrirExcluir = this.action((id: string) => {
            const t = s.templates.find((x) => x.id === id)
            s.excluir = t && podeExcluir(t) ? t : undefined
            s.erroExcluir = undefined
        }, this)
        s.onFecharExcluir = this.action(() => {
            s.excluir = undefined
            s.erroExcluir = undefined
        }, this)
        s.onExcluir = this.action(this.#excluir, this)

        s.onAbrirTrocar = this.action((uso: string) => {
            s.trocar = s.usos.find((u) => u.uso === uso)
            s.erroTrocar = undefined
        }, this)
        s.onFecharTrocar = this.action(() => {
            s.trocar = undefined
            s.erroTrocar = undefined
        }, this)
        s.onLigar = this.action(this.#ligar, this)
    }

    async #carregar(atualizar: boolean) {
        this.scope.carregando = true
        try {
            const [usos, templates] = await Promise.all([service.usos(atualizar), service.listar(atualizar)])
            this.scope.usos = usos
            this.scope.templates = templates
        } catch (error) {
            this.unexpected('Carregando os templates', error)
        } finally {
            this.scope.carregando = false
        }
    }

    #abrirCriar(uso: string) {
        const definicao = uso ? this.scope.usos.find((u) => u.uso === uso) : undefined
        this.scope.detalhe = undefined
        this.scope.editor = editorNovo(definicao)
        this.scope.erroEditor = undefined
    }

    #abrirEditar(id: string) {
        const template = this.scope.templates.find((t) => t.id === id)
        if (!template) return
        this.scope.detalhe = undefined
        this.scope.editor = editorDoTemplate(template)
        this.scope.erroEditor = undefined
    }

    async #salvarEditor() {
        const { scope } = this
        const editor = scope.editor
        if (!editor || scope.salvando) return
        const problemas = problemasDoEditor(
            editor,
            scope.templates.map((t) => t.nome)
        )
        if (problemas.length) {
            scope.erroEditor = problemas.join(' ')
            return
        }
        scope.salvando = true
        scope.erroEditor = undefined
        try {
            if (editor.modo === 'criar') {
                const criado = await service.criar(editor)
                const aviso = criado.simulado
                    ? `Template ${criado.nome} criado (simulado no desenvolvimento).`
                    : `Template ${criado.nome} enviado à Meta: ${
                          criado.status === 'APPROVED' ? 'aprovado' : 'em análise'
                      }.`
                this.app.notify('success', aviso, 5000)
            } else {
                await service.editar(editor)
                this.app.notify('success', `Template ${editor.nome} editado; a Meta revisa de novo.`, 5000)
            }
            scope.editor = undefined
            await this.#carregar(true)
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) scope.erroEditor = mensagem
            else this.unexpected('Salvando o template', error)
        } finally {
            scope.salvando = false
        }
    }

    async #excluir() {
        const { scope } = this
        const template = scope.excluir
        if (!template || scope.salvando) return
        scope.salvando = true
        scope.erroExcluir = undefined
        try {
            await service.excluir(template.id)
            this.app.notify('success', `Template ${template.nome} (${template.idioma}) excluído.`, 5000)
            scope.excluir = undefined
            await this.#carregar(false)
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) scope.erroExcluir = mensagem
            else this.unexpected('Excluindo o template', error)
        } finally {
            scope.salvando = false
        }
    }

    async #ligar(escolha: string) {
        const { scope } = this
        const uso = scope.trocar
        if (!uso || scope.salvando) return
        const [template, idioma] = escolha.split('|')
        scope.salvando = true
        scope.erroTrocar = undefined
        try {
            await service.ligar(uso.uso, template, idioma)
            this.app.notify('success', `${uso.descricao}: agora usa ${template}.`, 5000)
            scope.trocar = undefined
            await this.#carregar(false)
        } catch (error) {
            const mensagem = mensagemDeNegocio(error)
            if (mensagem) scope.erroTrocar = mensagem
            else this.unexpected('Trocando o template do uso', error)
        } finally {
            scope.salvando = false
        }
    }
}

export default TemplatesPresenter
